// Phase 3 §3 — Post-login cinematic. Fires once per session immediately
// after a successful login (Login.jsx sets the sessionStorage flag that
// Shell.jsx consumes). Three pieces:
//
//   1. Full-screen overlay with the "Welcome to Gabruz Universe" banner,
//      animated in via CSS keyframes (zoom + fade + glow).
//   2. Fusion bhangra audio (looped fade) — file lives at public/welcome.mp3.
//      Browsers may block autoplay; we attempt to play() in a useEffect and
//      gracefully ignore the rejected promise. The login click itself is a
//      user gesture, so playback usually succeeds.
//   3. The underlying Command Center slow-opens behind the banner via the
//      `.app--welcoming` body class (see styles.css §Phase3 block).
//
// Total runtime: 4.5s. Auto-dismisses via onDone callback.

import React, { useEffect, useRef } from "react";

const WELCOME_AUDIO_SRC = "/welcome.mp3";
const RUNTIME_MS = 4500;

export default function WelcomeOverlay({ onDone }) {
  const audioRef = useRef(null);
  const dismissedRef = useRef(false);

  useEffect(() => {
    // Attempt to play the bhangra track. If autoplay is blocked, the
    // promise rejects — the cinematic still runs, just silently.
    const audio = audioRef.current;
    if (audio) {
      audio.volume = 0.6;
      audio.play().catch(() => { /* autoplay blocked — silent fallback */ });
    }

    const dismiss = () => {
      if (dismissedRef.current) return;
      dismissedRef.current = true;
      // Fade audio out alongside the banner so it doesn't cut hard.
      if (audio) {
        const fadeStart = audio.volume;
        const fadeStep = fadeStart / 12;
        let i = 0;
        const fadeTimer = setInterval(() => {
          i++;
          audio.volume = Math.max(0, fadeStart - fadeStep * i);
          if (i >= 12) { clearInterval(fadeTimer); try { audio.pause(); } catch {} }
        }, 60);
      }
      onDone?.();
    };

    const timer = setTimeout(dismiss, RUNTIME_MS);
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKey);
    };
  }, [onDone]);

  return (
    <div className="welcomeOverlay" role="dialog" aria-label="Welcome">
      <audio
        ref={audioRef}
        src={WELCOME_AUDIO_SRC}
        autoPlay
        loop
        preload="auto"
      />
      <div className="welcomeOverlay-glow welcomeOverlay-glow--a" />
      <div className="welcomeOverlay-glow welcomeOverlay-glow--b" />
      <div className="welcomeOverlay-content">
        <div className="welcomeOverlay-prefix">Initiating Command Center</div>
        <h1 className="welcomeOverlay-title">
          Welcome to <span>Gabruz Universe</span>
        </h1>
        <div className="welcomeOverlay-pulse">
          <span /><span /><span />
        </div>
      </div>
    </div>
  );
}
